import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  garagesApi,
  usersApi,
  uploadUrl,
  type FeaturedGarageEntry,
  type TopContributorApi,
} from '../../api/client';
import { trackEvent } from '../../analytics';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import type { Spot } from '../../types';
import styles from './Home.module.css';

function getSpotReactionCount(spot: Spot) {
  return (
    (spot.likeCount ?? spot.likes?.length ?? 0) +
    (spot.beautifulCount ?? spot.beautiful?.length ?? 0) +
    (spot.coolCount ?? spot.cool?.length ?? 0) +
    (spot.sweetPhotoCount ?? spot.sweetPhoto?.length ?? 0) +
    (spot.commentCount ?? spot.comments?.length ?? 0)
  );
}

function getSpotImageSrc(spot: Spot) {
  const src = spot.images?.[0] ?? spot.imageUrl;
  if (!src) return '';
  return src.startsWith('http') ? src : uploadUrl(src);
}

function getGarageImageSrc(entry: FeaturedGarageEntry) {
  if (!entry.previewPhotoUrl) return '';
  return entry.previewPhotoUrl.startsWith('http')
    ? entry.previewPhotoUrl
    : uploadUrl(entry.previewPhotoUrl);
}

function formatCount(value: number) {
  return new Intl.NumberFormat('en-US', {
    notation: value >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatSpotMeta(spot: Spot) {
  return [spot.city, spot.event].filter(Boolean).join(' · ') || 'Shared with the community';
}

const PILLARS = [
  {
    title: 'Spot rare cars quickly',
    description:
      'Upload photos, let RideSpot help identify the car, and post a sighting while the moment is still fresh.',
  },
  {
    title: 'Build a garage worth revisiting',
    description:
      'Turn your profile into a living garage with specs, photos, build notes, and your full ownership story.',
  },
  {
    title: 'Earn recognition from other enthusiasts',
    description:
      'Leaderboards, favorites, reactions, and profiles make it easy for great contributors to stand out.',
  },
];

export function Home() {
  const { spots, spotStats, loading, error } = useData();
  const { isAuthenticated } = useAuth();
  const [garageHighlights, setGarageHighlights] = useState<FeaturedGarageEntry[]>([]);
  const [topContributors, setTopContributors] = useState<TopContributorApi[]>([]);
  const [communityLoading, setCommunityLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCommunity = async () => {
      setCommunityLoading(true);
      const [featuredResult, contributorsResult] = await Promise.allSettled([
        garagesApi.featured(),
        usersApi.topContributors(),
      ]);

      if (cancelled) return;

      if (featuredResult.status === 'fulfilled') {
        const deduped = [...featuredResult.value.byEngagement, ...featuredResult.value.byCompleteness].filter(
          (entry, index, list) => list.findIndex((candidate) => candidate.user.id === entry.user.id) === index
        );
        setGarageHighlights(deduped.slice(0, 4));
      } else {
        setGarageHighlights([]);
      }

      if (contributorsResult.status === 'fulfilled') {
        setTopContributors(contributorsResult.value.contributors.slice(0, 4));
      } else {
        setTopContributors([]);
      }

      setCommunityLoading(false);
    };

    void loadCommunity();

    return () => {
      cancelled = true;
    };
  }, []);

  const sortedRecent = useMemo(
    () =>
      [...spots].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [spots]
  );

  const mostDiscussed = useMemo(
    () =>
      [...spots]
        .sort((a, b) => {
          const scoreDiff = getSpotReactionCount(b) - getSpotReactionCount(a);
          if (scoreDiff !== 0) return scoreDiff;
          return (b.viewCount ?? 0) - (a.viewCount ?? 0);
        })
        .slice(0, 4),
    [spots]
  );

  const freshSpots = sortedRecent.slice(0, 4);
  const heroSpot = mostDiscussed[0] ?? sortedRecent[0] ?? null;
  const uniqueSpotters = useMemo(
    () => new Set(spots.map((spot) => spot.authorId)).size,
    [spots]
  );
  const countryCount = useMemo(
    () => new Set(spots.map((spot) => spot.countryCode).filter(Boolean)).size,
    [spots]
  );

  const primaryCta = isAuthenticated
    ? { to: '/spot', label: 'Post a spot' }
    : { to: '/create-account', label: 'Create account' };
  const secondaryCta = isAuthenticated
    ? { to: '/garage', label: 'Build my garage' }
    : { to: '/garages/featured', label: 'Explore featured garages' };
  const viewSpotsCta = { to: '/feed', label: 'View Spots' };

  const handleCtaClick = (label: string, destination: string) => {
    trackEvent('cta_click', {
      context: 'home',
      label,
      destination,
      authenticated: isAuthenticated,
    });
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.kicker}>A home for car spotters, builders, and photographers</p>
          <h1 className={styles.heroTitle}>
            Share the cars you see, the cars you own, and the story behind both.
          </h1>
          <p className={styles.heroLead}>
            RideSpot helps enthusiasts turn quick sightings into lasting posts, build out public garages,
            and earn recognition from a community that actually notices the details.
          </p>
          <div className={styles.heroActions}>
            <Link
              to={primaryCta.to}
              className={styles.primaryAction}
              onClick={() => handleCtaClick(`hero_${primaryCta.label.toLowerCase().replace(/\s+/g, '_')}`, primaryCta.to)}
            >
              {primaryCta.label}
            </Link>
            <Link
              to={viewSpotsCta.to}
              className={styles.secondaryAction}
              onClick={() => handleCtaClick(`hero_${viewSpotsCta.label.toLowerCase().replace(/\s+/g, '_')}`, viewSpotsCta.to)}
            >
              {viewSpotsCta.label}
            </Link>
            <Link
              to={secondaryCta.to}
              className={styles.secondaryAction}
              onClick={() => handleCtaClick(`hero_${secondaryCta.label.toLowerCase().replace(/\s+/g, '_')}`, secondaryCta.to)}
            >
              {secondaryCta.label}
            </Link>
          </div>

          <div className={styles.statRow}>
            <article className={styles.statCard}>
              <strong>{formatCount(spotStats?.totalCount ?? spots.length)}</strong>
              <span>community spots</span>
            </article>
            <article className={styles.statCard}>
              <strong>{formatCount(spotStats?.uniqueSpotterCount ?? uniqueSpotters)}</strong>
              <span>spotters represented</span>
            </article>
            <article className={styles.statCard}>
              <strong>{formatCount(spotStats?.countryCount ?? countryCount)}</strong>
              <span>countries tagged</span>
            </article>
            <article className={styles.statCard}>
              <strong>{formatCount(garageHighlights.length)}</strong>
              <span>featured garages live</span>
            </article>
          </div>
        </div>

        <div className={styles.heroPanel}>
          {heroSpot ? (
            <Link
              to={`/spot/${heroSpot.id}`}
              className={styles.spotlightCard}
              onClick={() => trackEvent('content_card_click', { context: 'home_spotlight', type: 'spot' })}
            >
              <div className={styles.spotlightImageWrap}>
                <img
                  src={getSpotImageSrc(heroSpot)}
                  alt={`${heroSpot.car.make} ${heroSpot.car.model}`}
                  className={styles.spotlightImage}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className={styles.spotlightBody}>
                <p className={styles.spotlightLabel}>Community spotlight</p>
                <h2 className={styles.spotlightTitle}>
                  {[heroSpot.car.make, heroSpot.car.model, heroSpot.car.trim].filter(Boolean).join(' ')}
                </h2>
                <p className={styles.spotlightMeta}>{formatSpotMeta(heroSpot)}</p>
                {heroSpot.caption && <p className={styles.spotlightCaption}>{heroSpot.caption}</p>}
                <div className={styles.spotlightCounts}>
                  <span>♥ {heroSpot.likeCount ?? heroSpot.likes.length}</span>
                  <span>✨ {heroSpot.beautifulCount ?? (heroSpot.beautiful ?? []).length}</span>
                  <span>😎 {heroSpot.coolCount ?? (heroSpot.cool ?? []).length}</span>
                  <span>📷 {heroSpot.sweetPhotoCount ?? (heroSpot.sweetPhoto ?? []).length}</span>
                  <span>💬 {heroSpot.commentCount ?? heroSpot.comments.length}</span>
                </div>
              </div>
            </Link>
          ) : (
            <div className={styles.emptyPanel}>
              <h2>RideSpot is ready for founding contributors</h2>
              <p>
                Start the first wave of spots and garages so new visitors immediately see a living car community.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className={styles.pillars}>
        {PILLARS.map((pillar) => (
          <article key={pillar.title} className={styles.pillarCard}>
            <h2>{pillar.title}</h2>
            <p>{pillar.description}</p>
          </article>
        ))}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <p className={styles.sectionLabel}>Best first impression</p>
            <h2>Most talked-about spots right now</h2>
          </div>
          <Link
            to="/most-voted"
            className={styles.sectionLink}
            onClick={() => handleCtaClick('home_view_most_voted', '/most-voted')}
          >
            View most voted
          </Link>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {loading && mostDiscussed.length === 0 ? (
          <p className={styles.loading}>Loading spots…</p>
        ) : mostDiscussed.length === 0 ? (
          <div className={styles.emptyPanel}>
            <p>No spots yet. Be the first member to set the tone.</p>
          </div>
        ) : (
          <div className={styles.spotGrid}>
            {mostDiscussed.map((spot) => (
              <Link
                key={spot.id}
                to={`/spot/${spot.id}`}
                className={styles.spotCard}
                onClick={() => trackEvent('content_card_click', { context: 'home_most_discussed', type: 'spot' })}
              >
                <div className={styles.spotImageWrap}>
                  <img
                    src={getSpotImageSrc(spot)}
                    alt={`${spot.car.make} ${spot.car.model}`}
                    className={styles.spotImage}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className={styles.spotBody}>
                  <strong className={styles.spotTitle}>
                    {[spot.car.make, spot.car.model, spot.car.trim].filter(Boolean).join(' ')}
                  </strong>
                  <span className={styles.spotMeta}>{formatSpotMeta(spot)}</span>
                  <span className={styles.spotCounts}>
                    {getSpotReactionCount(spot)} reactions · {spot.viewCount ?? 0} views
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <p className={styles.sectionLabel}>Owner side of the community</p>
            <h2>Featured garages worth exploring</h2>
          </div>
          <Link
            to="/garages/featured"
            className={styles.sectionLink}
            onClick={() => handleCtaClick('home_view_featured_garages', '/garages/featured')}
          >
            View featured garages
          </Link>
        </div>

        {communityLoading && garageHighlights.length === 0 ? (
          <p className={styles.loading}>Loading featured garages…</p>
        ) : garageHighlights.length === 0 ? (
          <div className={styles.emptyPanel}>
            <p>Featured garages will appear here as members fill out their builds and earn community engagement.</p>
          </div>
        ) : (
          <div className={styles.garageGrid}>
            {garageHighlights.map((entry) => {
              const imageSrc = getGarageImageSrc(entry);
              return (
                <Link
                  key={entry.user.id}
                  to={`/garages/@${entry.user.username}`}
                  className={styles.garageCard}
                  onClick={() => trackEvent('content_card_click', { context: 'home_featured_garage', type: 'garage' })}
                >
                  <div className={styles.garageImageWrap}>
                    {imageSrc ? (
                      <img src={imageSrc} alt="" className={styles.garageImage} loading="lazy" decoding="async" />
                    ) : (
                      <div className={styles.garagePlaceholder}>Garage preview</div>
                    )}
                  </div>
                  <div className={styles.garageBody}>
                    <strong>{entry.user.displayName}</strong>
                    <span>
                      {entry.garageCarCount} car{entry.garageCarCount !== 1 ? 's' : ''}
                    </span>
                    <span>
                      {(entry.engagementScore ?? 0) > 0
                        ? `${Math.round(entry.engagementScore ?? 0)} engagement score`
                        : `${Math.round(entry.completenessScore ?? 0)} completeness points`}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <p className={styles.sectionLabel}>Social proof for new members</p>
            <h2>People already setting the pace</h2>
          </div>
          <Link
            to="/top-contributors"
            className={styles.sectionLink}
            onClick={() => handleCtaClick('home_view_top_contributors', '/top-contributors')}
          >
            View all-time legends
          </Link>
        </div>

        {communityLoading && topContributors.length === 0 ? (
          <p className={styles.loading}>Loading contributors…</p>
        ) : topContributors.length === 0 ? (
          <div className={styles.emptyPanel}>
            <p>The first standout contributors will show up here as the community grows.</p>
          </div>
        ) : (
          <ol className={styles.leaderList}>
            {topContributors.map((contributor, index) => (
              <li key={contributor.user.id} className={styles.leaderRow}>
                <span className={styles.rank}>{index + 1}</span>
                <Link
                  to={`/user/${contributor.user.id}`}
                  className={styles.leaderCard}
                  onClick={() => trackEvent('content_card_click', { context: 'home_top_contributors', type: 'profile' })}
                >
                  <div className={styles.leaderText}>
                    <strong>{contributor.user.displayName}</strong>
                    <span>
                      {Math.round(contributor.contributorScore)} score · {contributor.spotCount} spot
                      {contributor.spotCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className={styles.leaderDetail}>
                    ♥ {contributor.likedCount} · ✨ {contributor.beautifulCount} · 😎 {contributor.coolCount}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <p className={styles.sectionLabel}>Fresh activity</p>
            <h2>New from the RideSpot feed</h2>
          </div>
          <div className={styles.sectionActions}>
            <Link
              to="/feed"
              className={styles.sectionCta}
              onClick={() => handleCtaClick('home_view_full_feed', '/feed')}
            >
              View full feed
            </Link>
            <Link
              to="/search"
              className={styles.sectionLink}
              onClick={() => handleCtaClick('home_view_search', '/search')}
            >
              Search spots
            </Link>
          </div>
        </div>

        {loading && freshSpots.length === 0 ? (
          <p className={styles.loading}>Loading fresh spots…</p>
        ) : freshSpots.length === 0 ? (
          <div className={styles.emptyPanel}>
            <p>No recent spots yet. Your next upload could become the first featured story here.</p>
          </div>
        ) : (
          <div className={styles.spotGrid}>
            {freshSpots.map((spot) => (
              <Link
                key={spot.id}
                to={`/spot/${spot.id}`}
                className={styles.spotCard}
                onClick={() => trackEvent('content_card_click', { context: 'home_fresh_spots', type: 'spot' })}
              >
                <div className={styles.spotImageWrap}>
                  <img
                    src={getSpotImageSrc(spot)}
                    alt={`${spot.car.make} ${spot.car.model}`}
                    className={styles.spotImage}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className={styles.spotBody}>
                  <strong className={styles.spotTitle}>
                    {[spot.car.make, spot.car.model, spot.car.trim].filter(Boolean).join(' ')}
                  </strong>
                  <span className={styles.spotMeta}>{formatSpotMeta(spot)}</span>
                  <span className={styles.spotCounts}>
                    {spot.commentCount ?? spot.comments.length} comments · {spot.viewCount ?? 0} views
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className={styles.ctaBand}>
        <div>
          <p className={styles.sectionLabel}>Why join now</p>
          <h2>Early contributors shape the culture and get remembered.</h2>
          <p className={styles.ctaBandCopy}>
            Founding members get the cleanest shot at standout profiles, featured garages, and leaderboard visibility.
          </p>
        </div>
        <div className={styles.ctaBandActions}>
          <Link
            to={primaryCta.to}
            className={styles.primaryAction}
            onClick={() => handleCtaClick(`band_${primaryCta.label.toLowerCase().replace(/\s+/g, '_')}`, primaryCta.to)}
          >
            {primaryCta.label}
          </Link>
          <Link
            to="/about"
            className={styles.ghostAction}
            onClick={() => handleCtaClick('band_learn_more', '/about')}
          >
            Learn what makes RideSpot different
          </Link>
        </div>
      </section>
    </div>
  );
}
